import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../contexts/AuthContext';
import { useTenant } from '../../../contexts/TenantContext';
import { supabase } from '../../../lib/supabase';
import Sidebar from '../../admin-dashboard/components/Sidebar';

/**
 * Admin V2 Violations — Liste read-only des violations (table réelle).
 * Route : /admin/violations (V2Switcher) + /admin/v2/violations.
 * Feature : pages.violations.admin (OFF par défaut — alimentée par scraping Phase 2).
 *
 * Lit la table `violations` (existe déjà). En Phase 1 sans scraping elle est
 * généralement vide → empty state explicite. Quand le scraping alimente la
 * table (Phase 2), la liste s'affiche sans changement de code.
 */
const STATUS_FILTERS = ['', 'open', 'closed', 'appealed'];

const AdminV2Violations = () => {
  const { t } = useTranslation();
  const { userProfile } = useAuth();
  const { tenantName, tenantId, agencyId } = useTenant();
  const [collapsed, setCollapsed] = useState(false);
  const [rows, setRows] = useState([]);
  const [creatorMap, setCreatorMap] = useState(new Map());
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!tenantId) return;
    setLoading(true); setError(null);
    try {
      let q = supabase
        .from('violations')
        .select('id, creator_id, type, status, ban_duration_days, detected_at, appealed_at, appeal_note')
        .eq('tenant_id', tenantId)
        .order('detected_at', { ascending: false });
      if (agencyId) q = q.eq('agency_id', agencyId);
      if (statusFilter) q = q.eq('status', statusFilter);
      const { data, error: e1 } = await q.limit(1000);
      if (e1) throw e1;
      setRows(data || []);

      const ids = Array.from(new Set((data || []).map((r) => r.creator_id).filter(Boolean)));
      const map = new Map();
      if (ids.length > 0) {
        const { data: c } = await supabase
          .from('black_creators').select('id, display_name, tiktok_username').in('id', ids);
        for (const cr of c || []) map.set(cr.id, cr);
      }
      setCreatorMap(map);
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, [tenantId, agencyId, statusFilter]);

  useEffect(() => { load(); }, [load]);

  const statusCls = (s) =>
    s === 'open' ? 'bg-error/10 text-error'
  : s === 'appealed' ? 'bg-warning/10 text-warning'
  : s === 'closed' ? 'bg-muted text-muted-foreground'
  : 'bg-muted text-muted-foreground';

  return (
    <div className="flex min-h-screen bg-background text-foreground">
      <Sidebar collapsed={collapsed} onToggle={() => setCollapsed((c) => !c)} userEmail={userProfile?.email} />
      <main className="flex-1 min-w-0 overflow-x-hidden">
        <header className="border-b border-border bg-card sticky top-0 z-10">
          <div className="px-4 md:px-6 py-4 flex items-center justify-between gap-3 flex-wrap">
            <div>
              <h1 className="text-xl md:text-2xl font-heading font-bold text-foreground">{t('violationsV2.title', 'Violations')}</h1>
              <p className="text-xs text-muted-foreground mt-0.5">{tenantName} · {rows.length}</p>
            </div>
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-2 rounded-lg bg-card border border-border text-base focus:outline-none focus:ring-1 focus:ring-primary">
              {STATUS_FILTERS.map((s) => <option key={s} value={s}>{t(`violationsV2.status.${s || 'all'}`, s || 'All')}</option>)}
            </select>
          </div>
        </header>
        <div className="p-4 md:p-6 space-y-4">
          <div className="rounded-2xl bg-primary/5 border border-primary/20 p-4 text-xs text-muted-foreground">
            <span className="font-semibold text-primary mr-1">{t('violationsV2.phase2Label', 'Phase 2:')}</span>
            {t('violationsV2.phase2Note', 'Violations are populated by the scraping pipeline. This page reads the violations table live — empty until scraping is wired for this tenant.')}
          </div>

          {error && (
            <div className="rounded-2xl bg-error/5 border border-error/30 p-4 text-sm text-error flex items-center justify-between">
              <span>{String(error.message || error)}</span>
              <button type="button" onClick={load} className="px-3 py-1.5 rounded-lg border border-error/40 hover:bg-error/10 text-xs font-medium">{t('common.retry', 'Retry')}</button>
            </div>
          )}

          {loading ? (
            <div className="rounded-2xl bg-card border border-border h-48 skeleton" />
          ) : rows.length === 0 ? (
            <div className="rounded-2xl bg-card border border-border p-8 text-center text-sm text-muted-foreground">
              {t('violationsV2.empty', 'No violations recorded (scraping not active).')}
            </div>
          ) : (
            <section className="rounded-2xl bg-card border border-border overflow-hidden">
              <ul className="divide-y divide-border">
                {rows.map((v) => {
                  const c = creatorMap.get(v.creator_id);
                  return (
                    <li key={v.id} className="px-4 md:px-5 py-3 flex items-center justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <Link to={`/admin/v2/creators/${v.creator_id}`} className="font-medium text-foreground hover:text-primary truncate block">
                          {c?.display_name || c?.tiktok_username || '—'}
                        </Link>
                        <div className="text-xs text-muted-foreground truncate">
                          {v.type} · {v.detected_at ? new Date(v.detected_at).toLocaleDateString() : ''}
                          {v.ban_duration_days ? ` · ${v.ban_duration_days}d ban` : ''}
                        </div>
                      </div>
                      <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full shrink-0 ${statusCls(v.status)}`}>
                        {t(`violationsV2.status.${v.status}`, v.status)}
                      </span>
                    </li>
                  );
                })}
              </ul>
            </section>
          )}
        </div>
      </main>
    </div>
  );
};

export default AdminV2Violations;
